<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <v-app>
    <!-- Top Navigation Bar -->
    <v-app-bar :color="colors.primaryDark" dark elevation="4" height="60">
      <div class="d-flex align-center">
        <v-icon size="40" class="ml-7 mr-2 pulse">fas fa-building</v-icon>
        <span class="text-h6 font-weight-bold gradient-text">
          SICON PAINEL DE CONTROLE I.A
        </span>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center" style="gap: 24px">
        <!-- User Profile -->
        <div class="d-flex align-center mr-12 user-profile" style="gap: 7px">
          <v-avatar size="40" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="20">fas fa-user</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2">
              {{ userDetails?.name || 'Carregando...' }}
            </span>
            <span class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Online' }}
            </span>
          </div>
        </div>
      </div>
    </v-app-bar>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :color="colors.primaryDark"
      dark
      permanent
      width="250"
      class="custom-drawer"
    >
      <!-- User Profile in Sidebar -->
      <div class="pa-4 sidebar-profile" style="border-bottom: 1px solid #1a4b7a">
        <div class="d-flex align-center" style="gap: 12px">
          <v-avatar size="45" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="22">fas fa-user</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-bold text-body-2 lowercase">
              {{ userDetails?.name || 'Carregando...' }}
            </div>
            <div class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Sistema Online' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      <v-list nav class="pa-0 text-start">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :value="item.value"
          :active="item.active"
          rounded="xl"
          class="mb-2 menu-item"
          :class="item.active ? 'active-item' : ''"
          lines="one"
          density="comfortable"
          @click="router.push(item.route)"
        >
          <template #prepend>
            <v-icon size="22" class="mr-2">{{ item.icon }}</v-icon>
          </template>

          <template #title>
            <span class="item-title">{{ item.title }}</span>
          </template>

          <template #append v-if="item.badge">
            <v-chip size="small" :color="item.badgeColor" class="pulse-badge">
              {{ item.badge }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-3" style="border-top: 1px solid #1a4b7a">
          <v-btn
            block
            variant="outlined"
            prepend-icon="fas fa-sign-out-alt"
            class="logout-btn"
            @click="logout"
          >
            Sair
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main class="bg-grey-lighten-5 main-content" style="margin-top: 20px">
      <!-- WhatsApp Status Content -->
      <v-container fluid class="pa-6">
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8">
            <!-- Header da página -->
            <div class="mb-8">
              <v-row align="center" class="mb-4">
                <v-col cols="auto">
                  <v-avatar size="64" :color="colors.primaryLight" class="elevation-4">
                    <v-icon color="white" size="32">fab fa-whatsapp</v-icon>
                  </v-avatar>
                </v-col>
                <v-col>
                  <h1 class="text-h3 font-weight-bold" :style="{ color: colors.primaryDark }">
                    Sicon chat
                  </h1>
                  <p class="text-subtitle-1 text-grey-darken-1 ma-0">
                    Gerenciamento de conexão do WhatsApp Web
                  </p>
                </v-col>
              </v-row>
            </div>

            <!-- Status Card -->
            <v-card class="mb-6 elevation-8" rounded="xl">
              <v-card-title 
                class="pa-6 text-white"
                :style="{
                  background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primaryLight} 100%)`
                }"
              >
                <v-icon class="mr-2">fas fa-signal</v-icon>
                Status da Conexão
              </v-card-title>

              <v-card-text class="pa-8">
                <!-- Loading State -->
                <div v-if="whatsappStatus.loading" class="text-center">
                   <v-avatar size="80" :color="colors.primaryGreen" class="elevation-4 mb-4"/>
                    <h3 class="text-h4 font-weight-bold text-green-darken-1 mb-2">
                    {{ whatsappStatus.state === "connecting" ? "CONECTANDO..." : "CARREGANDO..." }}
                  </h3>
                  <p class="text-h6 text-grey-darken-1 font-weight-medium">
                    {{ whatsappStatus.state === "connecting" ? "Iniciando nova sessão do WhatsApp..." : "Verificando status do WhatsApp..." }}
                  </p>
                  <div class="d-flex justify-center ga-4 flex-wrap"></div>
                </div>

                <!-- Connected State -->
                <div v-else-if="whatsappStatus.state === 'connected'" class="text-center">
                  <v-avatar size="80" :color="colors.primaryGreen" class="elevation-4 mb-4">
                    <v-icon color="white" size="40">fas fa-check-circle</v-icon>
                  </v-avatar>
                  <h3 class="text-h4 font-weight-bold text-green-darken-1 mb-2">
                    CONECTADO
                  </h3>
                  <p class="text-body-1 text-grey-darken-1 mb-6">
                    WhatsApp Web está conectado e funcionando perfeitamente.
                  </p>
                  <div class="d-flex justify-center ga-4 flex-wrap">
                    <v-btn
                      :color="colors.primaryLight"
                      size="large"
                      class="elevation-4"
                      prepend-icon="fas fa-comments"
                    >
                      Ver Conversas
                    </v-btn>
                    <v-btn
                      variant="outlined"
                      :color="colors.primaryDark"
                      size="large"
                      prepend-icon="fas fa-sync-alt"
                      @click="reconnectWhatsApp"
                    >
                      Reconectar WhatsApp
                    </v-btn>
                  </div>
                </div>

                <!-- Disconnected State -->
                <div v-else-if="whatsappStatus.state === 'disconnected' && !whatsappStatus.qrCode" class="text-center">
                  <v-avatar size="80" color="error" class="elevation-4 mb-4">
                    <v-icon color="white" size="40">fas fa-times-circle</v-icon>
                  </v-avatar>
                  <h3 class="text-h4 font-weight-bold text-error mb-2">
                    DESCONECTADO
                  </h3>
                  <p class="text-body-1 text-grey-darken-1 mb-6">
                    WhatsApp Web não está conectado. Clique em "Reconectar WhatsApp" para iniciar.
                  </p>
                  <v-btn
                    :color="colors.primaryLight"
                    size="large"
                    class="elevation-4"
                    prepend-icon="fas fa-power-off"
                    @click="reconnectWhatsApp"
                  >
                    Reconectar WhatsApp
                  </v-btn>
                </div>

                <!-- QR Code State -->
                <div v-else-if="whatsappStatus.state === 'waiting_qr' && whatsappStatus.qrCode" class="text-center">
                  <h3 class="text-h4 font-weight-bold mb-2" :style="{ color: colors.primaryDark }">
                    Escaneie o QR Code
                  </h3>
                  <p class="text-body-1 text-grey-darken-1 mb-6">
                    Use o WhatsApp do seu celular para escanear o código abaixo:
                  </p>
                  
                  <!-- Card do QR Code -->
                  <v-card 
                    class="d-inline-block pa-6 elevation-8 mb-6"
                    :style="{ border: `4px solid ${colors.primaryLight}` }"
                    rounded="xl"
                  >
                    <!-- Exibe o QR Code se existir -->
                    <img 
                      v-if="whatsappStatus.qrCode"
                      :src="whatsappStatus.qrCode" 
                      alt="QR Code WhatsApp" 
                      style="width: 280px; height: 280px; display: block;"
                      @error="handleImageError"
                    />
                    
                    <!-- Fallback caso tenha erro -->
                    <div 
                      v-else 
                      class="d-flex align-center justify-center" 
                      style="width: 280px; height: 280px;"
                    >
                      <div class="text-center">
                        <v-progress-circular
                          indeterminate
                          :color="colors.primaryLight"
                          size="64"
                          class="mb-4"
                        ></v-progress-circular>
                        <p class="text-grey-darken-1">Gerando QR Code...</p>
                      </div>
                    </div>
                  </v-card>

                  <!-- Informações de debug (remover em produção) -->
                  <v-alert
                    v-if="false"
                    type="info"
                    variant="outlined"
                    class="mb-4 text-left"
                    dense
                  >
                    <small>
                      <strong>Debug:</strong><br>
                      QR Code presente: {{ !!whatsappStatus.qrCode }}<br>
                      Tamanho: {{ whatsappStatus.qrCode?.length || 0 }} caracteres<br>
                      Formato: {{ whatsappStatus.qrCode?.substring(0, 30) }}...
                    </small>
                  </v-alert>

                  <v-alert
                    type="info"
                    variant="tonal"
                    class="mb-4"
                    rounded="lg"
                  >
                    <v-alert-title class="font-weight-bold">Como conectar:</v-alert-title>
                    <ol class="text-left mt-2">
                      <li>Abra o WhatsApp no seu celular</li>
                      <li>Toque em Menu (⋮) ou Configurações</li>
                      <li>Toque em "Aparelhos conectados"</li>
                      <li>Toque em "Conectar um aparelho"</li>
                      <li>Aponte a câmera para este QR code</li>
                    </ol>
                  </v-alert>

                  <v-btn
                    variant="outlined"
                    :color="colors.primaryDark"
                    prepend-icon="fas fa-sync-alt"
                    size="large"
                    @click="reconnectWhatsApp"
                  >
                    Reconectar WhatsApp
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <!-- Info Cards -->
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<!-- Ajustes no script setup -->
<script setup>
import { ref, onMounted, onUnmounted } from "vue";

import { useRouter } from 'vue-router';
import axios from "axios";
import { useAuthStore } from '@/stores/auth';
import { menuItems } from '@/constants/MenuItems';
import { io } from 'socket.io-client';

const router = useRouter();
const authStore = useAuthStore();
const token = authStore?.token || null;

const drawer = ref(true);
const userDetails = ref({});

const WA_STATES = {
  CONNECTING: "connecting",
  WAITING_QR: "waiting_qr",
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
};

// WhatsApp Status
const whatsappStatus = ref({
  state: WA_STATES.CONNECTING,
  ready: false,
  qrCode: null,
  loading: true,
  connected: false,
});

let socket = null;
let qrInterval = null;

const CONNECTED_STATUS_VALUES = new Set([
  "connected",
  "conectado",
  "conetado",
  "ready",
  "authenticated",
  "online",
]);

// Config
const API_BASE = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
const SOCKET_URL = (process.env.VUE_APP_SOCKET_URL || API_BASE).replace(/\/$/, '');

// Axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: token ? { Authorization: `Bearer ${token}` } : {},
  withCredentials: false
});

const normalizeConnectionValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const normalizeQrDataUrl = (value) => {
  if (!value || typeof value !== "string") return null;
  return value.startsWith("data:image") ? value : `data:image/png;base64,${value}`;
};

const isConnectedPayload = (payload) => {
  if (!payload || typeof payload !== "object") return false;

  const booleanFlags = [
    payload.ready,
    payload.connected,
    payload.isReady,
    payload.isConnected,
    payload.authenticated,
    payload.isAuthenticated,
  ];

  if (booleanFlags.some((flag) => flag === true)) {
    return true;
  }

  const statusValues = [
    payload.status,
    payload.state,
    payload.connectionStatus,
    payload.sessionStatus,
  ];

  return statusValues.some((value) =>
    CONNECTED_STATUS_VALUES.has(normalizeConnectionValue(value))
  );
};

const setWaStatus = (state) => {
  const nextState = {
    ...whatsappStatus.value,
    state,
    loading: false,
  };

  if (state === WA_STATES.CONNECTING) {
    nextState.ready = false;
    nextState.connected = false;
    nextState.qrCode = null;
    nextState.loading = true;
  } else if (state === WA_STATES.WAITING_QR) {
    nextState.ready = false;
    nextState.connected = false;
    nextState.loading = false;
  } else if (state === WA_STATES.CONNECTED) {
    nextState.ready = true;
    nextState.connected = true;
    nextState.qrCode = null;
    nextState.loading = false;
  } else {
    nextState.ready = false;
    nextState.connected = false;
    nextState.qrCode = null;
    nextState.loading = false;
  }

  whatsappStatus.value = nextState;
};

const setQrDataUrl = (dataUrl) => {
  whatsappStatus.value = {
    ...whatsappStatus.value,
    qrCode: normalizeQrDataUrl(dataUrl),
    loading: false,
  };
};

const fetchConnectionStatus = async ({ preserveStateWhenNotReady = false } = {}) => {
  try {
    const resp = await api.get("/whatsapp/status");
    const ready = typeof resp?.data?.ready === "boolean"
      ? resp.data.ready
      : isConnectedPayload(resp?.data);

    if (ready) {
      setWaStatus(WA_STATES.CONNECTED);
      return true;
    }

    if (!preserveStateWhenNotReady) {
      setWaStatus(WA_STATES.DISCONNECTED);
    }
  } catch (err) {
    console.log("❌ Erro ao buscar status:", err.response?.status, err.message);
    if (!preserveStateWhenNotReady) {
      setWaStatus(WA_STATES.DISCONNECTED);
    }
  }
  return false;
};

// Métodos
const fetchUserDetails = async () => {
  try {
    const response = await api.get('/me');
    userDetails.value = response.data || {};
  } catch (error) {
    userDetails.value = null;
    console.warn('Erro ao buscar usuário:', error);
  }
};

const fetchQrOnce = async ({ preserveStateWhenMissingQr = false } = {}) => {
  try {
    const resp = await api.get('/whatsapp/qr');
    const qrCodeData =
      resp?.data?.dataUrl ||
      resp?.data?.qrCode ||
      resp?.data?.qr ||
      (typeof resp?.data === 'string' ? resp.data : null);

    if (qrCodeData) {
      setWaStatus(WA_STATES.WAITING_QR);
      setQrDataUrl(qrCodeData);
      return true;
    }

    await fetchConnectionStatus({
      preserveStateWhenNotReady: preserveStateWhenMissingQr,
    });
  } catch (err) {
    if (err.response?.status === 404) {
      await fetchConnectionStatus({
        preserveStateWhenNotReady: preserveStateWhenMissingQr,
      });
      return false;
    }

    console.log('❌ Erro ao buscar QR:', err.response?.status, err.message);
    await fetchConnectionStatus({
      preserveStateWhenNotReady: preserveStateWhenMissingQr,
    });
  }
  return false;
};

const startQrPolling = () => {
  if (qrInterval) return;
  console.log('🔄 Iniciando polling do QR Code...');

  qrInterval = setInterval(async () => {
    if (whatsappStatus.value.state === WA_STATES.CONNECTED) {
      stopQrPolling();
      return;
    }

    const found = await fetchQrOnce({ preserveStateWhenMissingQr: true });
    if (found) {
      stopQrPolling();
    }
  }, 3000);
};

const stopQrPolling = () => {
  if (qrInterval) {
    console.log('⏹️ Parando polling do QR Code');
    clearInterval(qrInterval);
    qrInterval = null;
  }
};

const logout = () => {
  if (authStore && typeof authStore.logout === 'function') {
    authStore.logout();
  }
  router.push('/');
};

const reconnectWhatsApp = async () => {
  try {
    await api.post("/whatsapp/relogin");
    setWaStatus(WA_STATES.CONNECTING);
  } catch (err) {
    console.warn("Erro ao reconectar WhatsApp:", err);
    setWaStatus(WA_STATES.DISCONNECTED);
    return;
  }

  const connected = await fetchConnectionStatus({
    preserveStateWhenNotReady: true,
  });
  if (connected) {
    return;
  }

  const hasQr = await fetchQrOnce({ preserveStateWhenMissingQr: true });
  if (!hasQr) {
    startQrPolling();
  }
};

const handleImageError = async () => {
  setQrDataUrl(null);
  const hasQr = await fetchQrOnce({ preserveStateWhenMissingQr: true });
  if (!hasQr) {
    startQrPolling();
  }
};

// Inicializa socket
const initSocket = () => {
  socket = io(SOCKET_URL, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    withCredentials: true,
    auth: token ? { token } : undefined,
    autoConnect: true,
  });

  socket.on('connect', async () => {
    console.log('🔌 Socket conectado', socket.id);
    if (whatsappStatus.value.state !== WA_STATES.CONNECTED) {
      await fetchConnectionStatus({
        preserveStateWhenNotReady:
          whatsappStatus.value.state === WA_STATES.CONNECTING ||
          whatsappStatus.value.state === WA_STATES.WAITING_QR,
      });
    }
  });

  socket.on('whatsapp:qr', (payload = {}) => {
    const dataUrl = payload?.dataUrl || payload?.qr || payload?.qrCode || payload || null;
    if (!normalizeQrDataUrl(dataUrl)) {
      setWaStatus(WA_STATES.CONNECTING);
      startQrPolling();
      return;
    }

    setWaStatus(WA_STATES.WAITING_QR);
    setQrDataUrl(dataUrl);
    stopQrPolling();
  });

  socket.on('whatsapp:ready', () => {
    setWaStatus(WA_STATES.CONNECTED);
    setQrDataUrl(null);
    socket.emit("whatsapp:fetch-conversations");
    stopQrPolling();
  });

  socket.on('whatsapp:authenticated', () => {
    setWaStatus(WA_STATES.CONNECTED);
    setQrDataUrl(null);
    socket.emit("whatsapp:fetch-conversations");
    stopQrPolling();
  });

  socket.on('whatsapp:disconnected', () => {
    setWaStatus(WA_STATES.DISCONNECTED);
  });

  socket.on('whatsapp:auth_failure', () => {
    setWaStatus(WA_STATES.DISCONNECTED);
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket desconectado:', reason);
    if (whatsappStatus.value.state === WA_STATES.CONNECTING) {
      startQrPolling();
    }
  });
};

// Lifecycle
onMounted(async () => {
  await fetchUserDetails();
  initSocket();

  const connected = await fetchConnectionStatus();
  if (!connected) {
    await fetchQrOnce();
  }
});

onUnmounted(() => {
  stopQrPolling();
  if (socket) {
    socket.disconnect();
    socket = null;
  }
});

const colors = {
  primaryDark: "#123A5D",
  primaryLight: "#57C2E3",
  primaryGreen: "#A3C940",
  lightBg: "#E7F2F9",
};
</script>
<style scoped>
/* Animations */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

/* Component Styles */
.gradient-text {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.pulse {
  animation: pulse 2s infinite;
}

.user-profile {
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 8px 12px;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.custom-drawer {
  background: linear-gradient(180deg, #123A5D 0%, #0f2a42 100%);
}

.menu-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 4px 0;
  position: relative;
  overflow: hidden;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  transform: translateX(4px);
}

.active-item {
  background: linear-gradient(135deg, #57C2E3, #4aa3c1) !important;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
}

.active-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: #A3C940;
}

.pulse-badge {
  animation: pulse 2s infinite;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.6);
}

.main-content {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8f4f8 100%);
}

.lowercase {
  text-transform: lowercase;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .user-profile span:last-child {
    display: none;
  }
}
@media (max-width: 600px) {
  .gradient-text {
    font-size: 1rem;
  }
}
</style>
